import {
    Button,
    Form, Input,
    PageHeader,
    Space,
} from 'antd';
import React, {
    useCallback, useContext,
    useEffect,
    useState,
} from 'react';
import { ETourTargetType, ETourType, EUrlMatchStrategy } from '../../../../enums/tours.enum';
import { ToursContext } from '../../context/tours.context';
import TourCommonFields from '../TourCommonFields';
import DashboardMirrorModal from './DashboardMirrorModal';
import UrlMatchingStrategySelector from './UrlMatchingStrategySelector';
import showTourConfirmModal from '../ConfirmModal';

interface SelectedElementState {
    selector: string;
}
/**
 * OnDemandTour component.
 * Renders UI to select an element from the Dashboard via DashboardMirror.
 * Stores the selected element's CSS selector and the startUrl (pathname) at selection time.
 */
const OnDemandTour: React.FC = () => {
    const {
        countries,
        loadingCountries,
        fetchWorkspacesAsync,
        currentPath,
        tourSelected,
        saveTour,
        updateTour,
        savingTour,
    } = useContext(ToursContext);

    const [form] = Form.useForm();
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [isSelectionMode, setIsSelectionMode] = useState<boolean>(false);
    const [selectedElement, setSelectedElement] = useState<SelectedElementState | null>(null);
    const [startUrl, setStartUrl] = useState<string | null>(null);
    const [targetType, setTargetType] = useState<ETourTargetType>(ETourTargetType.COUNTRY);
    const [applyToNewWorkspacesOnly, setApplyToNewWorkspacesOnly] = useState<boolean>(false);
    const [urlMatchStrategy, setUrlMatchStrategy] = useState<string>('pathname');
    const [requiredParams, setRequiredParams] = useState<string[]>([]);

    const getCurrentQueryParams = useCallback((): string[] => {
        if (!currentPath) return [];

        try {
            const url = new URL(currentPath, window.location.origin);
            return Array.from(url.searchParams.keys());
        } catch {
            return [];
        }
    }, [currentPath]);

    const availableParams = getCurrentQueryParams();

    const handleOpenModal = useCallback((): void => {
        setIsModalOpen(true);
        setIsSelectionMode(false);
    }, []);

    const handleCloseModal = useCallback((): void => {
        setIsModalOpen(false);
        setIsSelectionMode(false);
    }, []);

    const handleToggleSelection = useCallback((): void => {
        setIsSelectionMode((prev) => !prev);
    }, []);

    const handleElementSelected = useCallback((selector: string): void => {
        setSelectedElement({ selector });
        setStartUrl(currentPath);
        setIsSelectionMode(false);
        setIsModalOpen(false);
        form.setFieldsValue({
            startUrl: currentPath,
            elementTarget: selector,
        });
    }, [currentPath, form]);

    const handleTargetTypeChange = (value: ETourTargetType) => {
        setTargetType(value);
        form.setFieldsValue({
            targetIds: [],
        });
    };

    const rules = [
        {
            required: true,
            message: 'This field is required',
        },
    ];

    const getCurrentQueryParamValues = (url: string, params: string[]): Record<string, string> => {
        try {
            const parsed = new URL(url, window.location.origin);
            const values: Record<string, string> = {};
            params.forEach((param) => {
                const value = parsed.searchParams.get(param);
                if (value) values[param] = value;
            });
            return values;
        } catch {
            return {};
        }
    };

    const handleConfirmSubmit = () => {
        const targetIds = form.getFieldValue('targetIds') || [];

        showTourConfirmModal({
            targetType,
            targetIds,
            countries,
            applyToNewWorkspacesOnly,
            tourSelected,
            onConfirm: () => form.submit(),
        });
    };

    const onFinish = (values: any) => {
        const payload = {
            ...values,
            type: ETourType.ON_DEMAND,
            startUrl: values.startUrl || startUrl || '',
            elementTarget: selectedElement?.selector || values.elementTarget || '',
            urlMatchStrategy,
            requiredQueryParams: requiredParams,
            queryParamValues:
            urlMatchStrategy === EUrlMatchStrategy.CONTAINS
                ? getCurrentQueryParamValues(values.startUrl, requiredParams)
                : {},
            applyToNewWorkspacesOnly: targetType === ETourTargetType.WORKSPACE ? applyToNewWorkspacesOnly : false,
            newWorkspaceReferenceDate: (() => {
                // Si no es WORKSPACE o applyToNewWorkspacesOnly es false → null
                if (targetType !== ETourTargetType.WORKSPACE || !applyToNewWorkspacesOnly) {
                    return null;
                }

                // Si applyToNewWorkspacesOnly es true:
                // - Si ya existe una fecha previa → mantenerla
                // - Si no existe (se activó por primera vez) → crear una nueva
                return tourSelected?.newWorkspaceReferenceDate || new Date();
            })(),
        };

        if (tourSelected) {
            updateTour(tourSelected._id as string, payload);
        } else {
            saveTour(payload);
        }
    };

    useEffect(() => {
        if (tourSelected) {
            const selectedTour = tourSelected as any;

            form.setFieldsValue({
                name: tourSelected.name,
                description: tourSelected.description,
                targetType: tourSelected.targetType,
                targetIds: tourSelected.targetIds,
                loomUrl: tourSelected.loomUrl || '',
                scribeUrl: tourSelected.scribeUrl || '',
                startUrl: tourSelected.startUrl || '',
                elementTarget: selectedTour.elementTarget || '',
            });

            setTargetType(tourSelected.targetType);
            setStartUrl(tourSelected.startUrl || null);

            const strategy = selectedTour.urlMatchStrategy || EUrlMatchStrategy.PATHNAME;
            setUrlMatchStrategy(strategy);

            const params = selectedTour.requiredQueryParams || [];
            setRequiredParams(params);

            if (selectedTour.elementTarget) {
                setSelectedElement({ selector: selectedTour.elementTarget });
            } else {
                setSelectedElement(null);
            }

            setApplyToNewWorkspacesOnly(!!selectedTour.applyToNewWorkspacesOnly);
        } else {
            form.resetFields();
            setSelectedElement(null);
            setStartUrl(null);
            setTargetType(ETourTargetType.COUNTRY);
            setUrlMatchStrategy(EUrlMatchStrategy.PATHNAME);
            setRequiredParams([]);
            setIsSelectionMode(false);
            setIsModalOpen(false);
            setApplyToNewWorkspacesOnly(false);
        }
    }, [tourSelected, form]);

    return (
        <>
            <PageHeader
                title="On Demand Tour"
                style={{ padding: 0 }}
                footer={(
                    <Form form={form} layout="vertical" onFinish={onFinish}>
                        <TourCommonFields
                            tourSelected={tourSelected}
                            countries={countries}
                            loadingCountries={loadingCountries}
                            fetchWorkspacesAsync={fetchWorkspacesAsync}
                            targetType={targetType}
                            onChangeTargetType={handleTargetTypeChange}
                            applyToNewWorkspacesOnly={applyToNewWorkspacesOnly}
                            onChangeApplyToNewWorkspaces={setApplyToNewWorkspacesOnly}
                            extraRightTop={(
                                <>
                                    <Form.Item label="Select dashboard element" rules={rules}>
                                        <Space>
                                            <Button type="default" onClick={handleOpenModal}>Select dashboard element</Button>
                                        </Space>
                                    </Form.Item>
                                    <Form.Item
                                        tooltip="Obtained from Dashboard Mirror when you select a dashboard element"
                                        name="startUrl"
                                        label="Where to start the tour"
                                        help="Obtained from Dashboard Mirror when you select a dashboard element"
                                        rules={rules}
                                    >
                                        <Input disabled placeholder="/home" type="text" value={startUrl || ''} />
                                    </Form.Item>
                                    <Form.Item name="elementTarget" label="Element target (from mirror)" rules={rules}>
                                        <Input placeholder=".btn-primary" type="text" readOnly disabled value={selectedElement?.selector || ''} />
                                    </Form.Item>
                                    <UrlMatchingStrategySelector
                                        value={urlMatchStrategy as EUrlMatchStrategy}
                                        onChange={setUrlMatchStrategy}
                                        availableParams={availableParams}
                                        selectedParams={requiredParams}
                                        onParamsChange={setRequiredParams}
                                    />
                                </>
                            )}
                        />
                    </Form>
                )}
                extra={[
                    <Button
                        block
                        type="primary"
                        size="small"
                        loading={savingTour}
                        onClick={handleConfirmSubmit}
                    >
                        {tourSelected ? 'Update' : 'Create'}
                    </Button>,
                ]}
            />

            <DashboardMirrorModal
                open={isModalOpen}
                onClose={handleCloseModal}
                selectionMode={isSelectionMode}
                onToggleSelection={handleToggleSelection}
                onElementSelected={handleElementSelected}
                isEditingTour={!!tourSelected}
                tourStartUrl={tourSelected?.startUrl}
            />
        </>
    );
};

export default OnDemandTour;
